import { outlinedInputClasses } from "@mui/material/OutlinedInput";
import { createTheme } from "@mui/material/styles";
import { pickersOutlinedInputClasses } from "@mui/x-date-pickers/PickersTextField";

import { brandColors } from "../tokens/colors";
import { elevation } from "../tokens/elevation";
import { radius } from "../tokens/radius";
import { statusSurfaces } from "../tokens/statusSurfaces";

import type {
  Theme,
  ThemeOptions,
  TypographyStyle,
} from "@mui/material/styles";
import type { PickerComponents } from "@mui/x-date-pickers/themeAugmentation";

type CssBaselineStyleOverrides = NonNullable<
  NonNullable<ThemeOptions["components"]>["MuiCssBaseline"]
>["styleOverrides"];

/** Surface teintée : une couleur de fond et l'encre lisible qui va dessus. */
export type OdysTintColor = {
  main: string;
  contrastText: string;
};

declare module "@mui/material/styles" {
  interface Theme {
    radius: typeof radius;
    elevation: typeof elevation;
  }
  interface ThemeOptions {
    radius?: typeof radius;
    elevation?: typeof elevation;
  }
  /**
   * Surface teal claire + son teal lisible en `contrastText`. Entrée de
   * palette à part entière (et non une clé ajoutée à `primary`) : `primary`
   * porte déjà `dark`, qui pilote le hover des boutons `contained` — mélanger
   * les deux rôles est précisément ce qui avait forcé odys à surcharger
   * `primary.dark` localement.
   *
   * Typé `{ main; contrastText }` et non `PaletteColor` : `createPalette`
   * n'applique `augmentColor` qu'aux six couleurs qu'il connaît, donc les
   * `light`/`dark` d'un `PaletteColor` seraient `undefined` à l'exécution.
   */
  interface Palette {
    primaryTint: OdysTintColor;
  }
  interface PaletteOptions {
    primaryTint?: OdysTintColor;
  }
  interface TypographyVariants {
    mono: TypographyStyle;
  }
  interface TypographyVariantsOptions {
    mono?: TypographyStyle;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    mono: true;
  }
}

/**
 * `@mui/x-date-pickers/themeAugmentation` n'augmente PAS `@mui/material/styles`
 * de lui-même — contrairement à celui de `x-data-grid`, il se contente
 * d'exporter l'interface `PickerComponents`. Sans ce branchement, les slots
 * `MuiPickers*` sont refusés par le typage de `components`.
 */
declare module "@mui/material/styles" {
  // Une interface vide est ici le but : c'est du declaration merging, elle
  // n'ajoute rien d'elle-même et se contente d'attacher `PickerComponents`.
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type -- declaration merging: the empty interface is exactly what attaches PickerComponents
  interface Components<Theme = unknown> extends PickerComponents<Theme> {}
}

/**
 * Slots que chaque app peut surcharger par-dessus le cœur partagé.
 *
 * Le cœur (palette de marque, ÉCHELLE DE TITRES, échelle de radius,
 * comportements boutons/inputs, tokens de statut) est fourni par DÉFAUT et
 * partagé. La POLICE et la TAILLE de base se déclarent explicitement par app
 * via `fontFamily` / `fontSize`. Toute autre clé de `ThemeOptions` (palette,
 * shape, components…) est acceptée et deep-mergée par-dessus le cœur : une app
 * marketing (catalog) et un outil interne dense (odys, admin, onboarding)
 * gardent ainsi leurs spécificités légitimes (palette, radius de base,
 * overrides de composants).
 *
 * En revanche, une app NE redéfinit PLUS `typography.h1`…`h6` : la densité d'un
 * outil interne s'obtient en descendant d'un cran dans l'échelle (variant `h4`
 * au lieu de `h2`), pas en réécrivant l'échelle. Un même nom de variante doit
 * désigner le même style dans toutes les apps.
 */
export type OdysThemeOptions = ThemeOptions & {
  /** Police de l'app (une CSS var chargée via `next/font`), ex. `'var(--font-poppins)'`. Défaut : Poppins. Un fallback `sans-serif` est ajouté automatiquement. */
  fontFamily?: string;
  /** Taille de police de base (px). Défaut : 14. */
  fontSize?: number;
};

/**
 * Pile monospace unique du monorepo. L'audit avait relevé 22 `fontFamily:
 * 'monospace'` sur 15 fichiers admin et 3 piles différentes : les identifiants
 * techniques (record ids, DSN, diffs) doivent tous se lire de la même façon.
 */
const MONO_FONT_FAMILY =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

/**
 * Épaisseur de la piste et du rail d'un `Slider`, en px.
 *
 * 2 et non les 4 de MUI : dans une colonne de filtres de 260px, un curseur de
 * plage se lit comme un réglage fin, et il surplombe parfois un histogramme
 * dont il ne doit pas masquer les premières barres.
 */
const SLIDER_TRACK_HEIGHT = 2;

/**
 * Injecte les tokens de statut (`statusSurfaces`) dans les règles globales, en
 * les composant ici avec celles de l'app plutôt qu'en confiant la fusion au
 * `deepmerge` de `createTheme` : celui-ci ne fusionne que des objets simples,
 * donc une `styleOverrides` sous forme de **fonction** remplacerait purement
 * les tokens du socle, faisant disparaître `.status-chip` (chips de statut
 * sans couleur).
 */
function withStatusSurfaces(
  appStyleOverrides: CssBaselineStyleOverrides,
): CssBaselineStyleOverrides {
  if (appStyleOverrides === undefined) return statusSurfaces;
  if (typeof appStyleOverrides === "function") {
    return (...args) => ({
      ...statusSurfaces,
      ...(appStyleOverrides(...args) as Record<string, unknown>),
    });
  }
  return {
    ...statusSurfaces,
    ...(appStyleOverrides as Record<string, unknown>),
  };
}

/**
 * Thème Odys partagé. Le cœur visuel — dont l'échelle de titres — est commun à
 * toutes les apps ; une app ajoute ses overrides de composants et sa palette
 * via `overrides` (deep-merge par-dessus le cœur).
 *
 * Pas de `responsiveFontSizes` : il recalcule chaque taille sur tous les
 * breakpoints, donc aucune valeur déclarée n'était jamais rendue telle quelle
 * (h1 tombait à 1.75rem sur mobile) et h3 finissait à la même taille que h4 —
 * l'échelle perdait un cran. L'échelle cible étant déjà modeste, elle est
 * servie littéralement.
 *
 * Prérequis côté app : charger Poppins via `next/font` avec
 * `variable: '--font-poppins'` sur le `<body>` (cf. layout des apps).
 */
export function createOdysTheme(overrides: OdysThemeOptions = {}): Theme {
  const {
    fontFamily = "var(--font-poppins)",
    fontSize = 14,
    ...rest
  } = overrides;

  const restWithStatusSurfaces: OdysThemeOptions = {
    ...rest,
    components: {
      ...rest.components,
      MuiCssBaseline: {
        ...rest.components?.MuiCssBaseline,
        styleOverrides: withStatusSurfaces(
          rest.components?.MuiCssBaseline?.styleOverrides,
        ),
      },
    },
  };

  return createTheme(
    {
      radius,
      elevation,
      palette: {
        mode: "light",
        primary: {
          main: brandColors.primary,
          light: brandColors.primaryLight,
          dark: brandColors.primaryDark,
          contrastText: brandColors.onPrimary,
        },
        primaryTint: {
          main: brandColors.primaryTint,
          contrastText: brandColors.onPrimaryTint,
        },
        secondary: {
          main: brandColors.secondary,
          dark: brandColors.secondaryDark,
          contrastText: brandColors.onSecondary,
        },
        error: {
          main: brandColors.error,
          light: brandColors.errorLight,
          dark: brandColors.errorDark,
          contrastText: brandColors.onSemantic,
        },
        warning: {
          main: brandColors.warning,
          light: brandColors.warningLight,
          dark: brandColors.warningDark,
          contrastText: brandColors.onSemantic,
        },
        success: {
          main: brandColors.success,
          light: brandColors.successLight,
          dark: brandColors.successDark,
          contrastText: brandColors.onSemantic,
        },
        info: {
          main: brandColors.info,
          light: brandColors.infoLight,
          dark: brandColors.infoDark,
          contrastText: brandColors.onSemantic,
        },
        background: {
          default: brandColors.backgroundDefault,
          paper: brandColors.backgroundPaper,
        },
        text: {
          primary: brandColors.textPrimary,
          secondary: brandColors.textSecondary,
          disabled: brandColors.textDisabled,
        },
        divider: brandColors.divider,
      },
      typography: {
        fontFamily: `${fontFamily}, sans-serif`,
        fontSize,
        // Échelle de titres unique (ratio ≈ 1,2), en `rem` littérales : elle ne
        // dépend donc pas du `fontSize` de base de l'app. Deux graisses
        // seulement — 400 pour l'affichage (h1–h3), 500 pour la structure
        // (h4–h6). Le 600 / 700 reste légitime sur les libellés d'interface,
        // les chips et les états d'erreur, mais ne fait pas partie de l'échelle
        // de titres : sur un `Typography variant="h*"`, la graisse vient du
        // thème, jamais d'un `sx`.
        h1: {
          fontSize: "2.5rem",
          fontWeight: 400,
          lineHeight: 1.15,
          letterSpacing: "-0.02em",
        },
        h2: {
          fontSize: "2rem",
          fontWeight: 400,
          lineHeight: 1.2,
          letterSpacing: "-0.015em",
        },
        h3: {
          fontSize: "1.5rem",
          fontWeight: 400,
          lineHeight: 1.3,
          letterSpacing: "-0.01em",
        },
        h4: {
          fontSize: "1.25rem",
          fontWeight: 500,
          lineHeight: 1.35,
          letterSpacing: "0",
        },
        h5: {
          fontSize: "1.125rem",
          fontWeight: 500,
          lineHeight: 1.4,
          letterSpacing: "0",
        },
        h6: {
          fontSize: "1rem",
          fontWeight: 500,
          lineHeight: 1.5,
          letterSpacing: "0",
        },
        subtitle1: { fontSize: "1rem", fontWeight: 400, lineHeight: 1.5 },
        subtitle2: { fontSize: "0.875rem", fontWeight: 500, lineHeight: 1.55 },
        button: { textTransform: "none", fontWeight: 500 },
        // Variante `mono` : `<Typography variant="mono">`. Seule la pile de
        // police change — un identifiant technique ne doit pas sauter aux yeux
        // plus qu'un libellé courant.
        mono: {
          fontFamily: MONO_FONT_FAMILY,
          fontSize: "0.875rem",
          lineHeight: 1.5,
          letterSpacing: "0",
        },
      },
      // Trois durées sanctionnées, reprises des slots MUI existants (valeurs
      // inchangées, donc aucun impact visuel) : `shortest` (150ms) pour les
      // retours d'état immédiats — survol, opacité, couleur ; `shorter`
      // (200ms) pour les déplacements courts — translate, box-shadow ;
      // `standard` (300ms) pour l'entrée/sortie d'une surface. Les autres
      // slots MUI restent disponibles mais ne font pas partie de l'échelle.
      transitions: {
        duration: {
          shortest: 150,
          shorter: 200,
          standard: 300,
        },
      },
      shape: {
        // Base unifiée = token md (8px). Toutes les apps héritent de cette
        // valeur ; les rayons explicites passent par l'échelle `radius.*`.
        borderRadius: 8,
      },
      components: {
        MuiInputBase: {
          styleOverrides: {
            // Prevent iOS Safari from zooming in when an input gains focus:
            // it only triggers below a 16px font-size. Forced on xs only;
            // desktop keeps MUI's native small/medium sizing.
            input: ({ theme }) => ({
              [theme.breakpoints.down("sm")]: {
                fontSize: "16px",
              },
            }),
          },
        },
        // Arbitrage P0.4 (issue #2503) : les champs sont BORDÉS par défaut, et
        // posés sur `background.paper` — sans quoi ils se confondent avec le
        // fond beige des pages. C'est l'ancien style admin, généralisé ; c'est
        // aussi ce qui rend inutiles les 43 lignes d'overrides « sans bordure »
        // d'onboarding.
        //
        // Les `:not()` sont nécessaires : sans eux, ces règles gagneraient sur
        // celles de MUI pour `.Mui-error` et `.Mui-disabled` (elles sont
        // appliquées après), et un champ en erreur reprendrait une bordure
        // grise au survol.
        MuiOutlinedInput: {
          styleOverrides: {
            root: ({ theme }) => ({
              "&:not(.Mui-disabled)": {
                backgroundColor: theme.palette.background.paper,
              },
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: brandColors.inputBorder,
              },
              [`&:not(.Mui-error):not(.Mui-disabled):hover .${outlinedInputClasses.notchedOutline}`]:
                {
                  borderColor: brandColors.inputBorderHover,
                },
              [`&.Mui-focused:not(.Mui-error) .${outlinedInputClasses.notchedOutline}`]:
                {
                  borderColor: theme.palette.primary.main,
                },
            }),
          },
        },
        // Un champ de DatePicker n'est PAS un `MuiOutlinedInput` : MUI X en
        // fournit son propre composant, avec ses propres classes et ses propres
        // défauts (bordure `rgba(0,0,0,0.23)` au repos, `text.primary` au
        // survol). Sans ce miroir, l'arbitrage P0.4 ne l'atteindrait pas et les
        // champs de date auraient une bordure plus foncée que les champs texte
        // dans les 4 apps qui utilisent des pickers (issue #2505).
        MuiPickersOutlinedInput: {
          styleOverrides: {
            root: ({ theme }) => ({
              "&:not(.Mui-disabled)": {
                backgroundColor: theme.palette.background.paper,
              },
              [`& .${pickersOutlinedInputClasses.notchedOutline}`]: {
                borderColor: brandColors.inputBorder,
              },
              [`&:not(.Mui-error):not(.Mui-disabled):hover .${pickersOutlinedInputClasses.notchedOutline}`]:
                {
                  borderColor: brandColors.inputBorderHover,
                },
              [`&.Mui-focused:not(.Mui-error) .${pickersOutlinedInputClasses.notchedOutline}`]:
                {
                  borderColor: theme.palette.primary.main,
                },
            }),
          },
        },
        // Piste et rail à 2px au lieu des 4px de MUI : les deux curseurs de la
        // colonne de filtres de catalog n'avaient pas la même épaisseur — l'un
        // surchargeait localement, l'autre gardait le défaut — et le plus épais
        // écrasait l'histogramme de prix qu'il surplombe (issue #2514).
        MuiSlider: {
          styleOverrides: {
            track: {
              height: SLIDER_TRACK_HEIGHT,
            },
            rail: {
              height: SLIDER_TRACK_HEIGHT,
            },
          },
        },
        MuiButtonBase: {
          defaultProps: {
            disableRipple: true,
          },
        },
        MuiButton: {
          defaultProps: {
            disableElevation: true,
            size: "medium",
          },
          styleOverrides: {
            root: {
              textTransform: "none",
            },
          },
        },
      },
    },
    restWithStatusSurfaces,
  );
}
